import { type ReactElement } from 'react';
import MuiTooltip, { type TooltipProps as MuiTooltipProps } from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AppTheme } from '@/shared/styles';
import { mergeSx } from '@/shared/utils/merge-sx.util';
import { Except } from '@/shared/types';
import { mergeSlotProps } from '@mui/material/utils';

type MuiTooltipSlotProps = NonNullable<MuiTooltipProps['slotProps']>;

type MuiTooltipSlotTooltipProps = NonNullable<MuiTooltipProps['slotProps']>['tooltip'];

type TooltipSlotProps = Except<MuiTooltipSlotProps, 'tooltip'> & {
  tooltip?: MuiTooltipSlotTooltipProps & {
    [dataAttribute: `data-${string}`]: string;
  };
};

export type TooltipProps = MuiTooltipProps & {
  children: ReactElement;
  /** Vertical distance (in pixels) between the tooltip and its anchor. */
  offset?: number;
  /** Horizontal shift (in pixels) of the tooltip relative to its anchor. */
  skidding?: number;
  /** Slot-level props forwarded to MUI tooltip slots. */
  slotProps?: TooltipSlotProps;
  /** Tooltip placement to use on mobile screens. */
  mobilePlacement?: MuiTooltipProps['placement'];
};

function getTooltipPlacement(
  isMobile: boolean,
  placement: MuiTooltipProps['placement'],
  mobilePlacement: MuiTooltipProps['placement'],
) {
  if (isMobile) {
    return mobilePlacement ?? 'top';
  }

  return placement ?? 'right-start';
}

export function Tooltip({
  children,
  title,
  offset = 0,
  skidding = 0,
  placement,
  mobilePlacement,
  slotProps,
  ...props
}: TooltipProps) {
  const isMobile = useMediaQuery('(max-width: 600px)');
  const tooltipPlacement = getTooltipPlacement(isMobile, placement, mobilePlacement);

  if (!title) {
    return children;
  }

  const {
    tooltip: tooltipSlotProps,
    arrow: arrowSlotProps,
    popper: popperSlotProps,
    transition: transitionSlotProps,
  } = slotProps ?? {};

  const popperProps = {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [skidding, offset],
        },
      },
    ],
  };
  const tooltipProps = {
    sx: mergeSx(styles.tooltip, isMobile && styles.tooltipMobile),
  };
  const arrowProps = {
    sx: styles.arrow,
  };

  return (
    <MuiTooltip
      slotProps={{
        popper: mergeSlotProps(popperSlotProps, popperProps),
        tooltip: mergeSlotProps(tooltipSlotProps, tooltipProps),
        arrow: mergeSlotProps(arrowSlotProps, arrowProps),
        transition: transitionSlotProps,
      }}
      title={title}
      enterTouchDelay={0}
      leaveTouchDelay={0}
      enterDelay={0}
      leaveDelay={0}
      placement={tooltipPlacement}
      {...props}
    >
      {children}
    </MuiTooltip>
  );
}

const styles = {
  tooltip: {
    display: 'flex',
    padding: '4px 8px',
    m: '0 !important',
    minHeight: 'auto !important',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: (theme: AppTheme) => theme.palette.primary.main,
    color: (theme: AppTheme) => theme.palette.primary.light,
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: '0px',
    transform: 'none',
  },
  tooltipMobile: {
    transform: 'translateY(-100%)',
  },
  arrow: {
    display: 'none',
  },
};
